import getFromCdn from '@anm/helpers/getFromCdn';
import { getFadeInAnimation } from '@anm/styles/helpers';
import styled, { css } from 'styled-components';

import { HoverIconVariant, ItemSize, ListItemProps, ListWrapperProps, SelectedItemVariant } from './types';

const hoverIconVariants: { [key in HoverIconVariant]: ReturnType<typeof css> } = {
  'right-corner': css`
    border: 1px solid #fff;
    top: 0;
    right: 0;
    transform: translate(25%, -25%);
  `,
  'top-line': css`
    top: -6px;
    left: calc(50% - 8px - 6.25%);
    border: 2px solid #fff;
  `
};

const sizes: { [key in ItemSize]: ReturnType<typeof css> } = {
  small: css`
    margin-bottom: 4px;
    &:not(:nth-child(5n + 5)) {
      margin-right: 4px;
    }

    img,
    video {
      width: 60px;
      height: 60px;
    }
  `,
  medium: css`
    margin-bottom: 8px;
    &:not(:nth-child(4n + 4)) {
      margin-right: 8px;
    }

    img,
    video {
      width: 75px;
      height: 75px;
    }
  `,
  big: css`
    margin-bottom: 8px;
    width: calc(50% - 8px);
    &:not(:nth-child(even)) {
      margin-right: 8px;
    }

    img,
    video {
      width: 136px;
      height: 76px;
    }
  `,
  '64x64': css`
    margin-bottom: 8px;
    &:not(:nth-child(4n + 4)) {
      margin-right: 8px;
    }

    img,
    video {
      width: 64px;
      height: 64px;
    }
  `,
  row_three: css`
    margin-bottom: 8px;
    &:not(:nth-child(3n + 3)) {
      margin-right: 8px;
    }

    img,
    video {
      width: 88px;
      height: 88px;
    }
  `,
  row_four: css`
    margin-bottom: 8px;
    &:not(:nth-child(4n + 4)) {
      margin-right: 8px;
    }

    img,
    video {
      width: 136px;
      height: 76px;
    }
  `,
  full_width: css`
    margin-bottom: 8px;
  `
};

const unselectIconStyles = css`
  &:after {
    display: block;
    content: url(${getFromCdn('images/icons/eye.svg')});
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const blueOverlay = css`
  &:before {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 5px;
    background: rgba(48, 174, 242, 0.5);
  }
`;

const borderedImage = css`
  img {
    border: 4px solid ${({ isSelected }: ListItemProps) => (isSelected ? '#30AEF2' : 'transparent')};
    border-radius: 5px;
  }
`;

const blueOverlayStyles = css`
  ${({ isSelected }: ListItemProps) => isSelected && blueOverlay}
  ${borderedImage};
`;

const variants: { [key in SelectedItemVariant]: ReturnType<typeof css> } = {
  'thin-border': css`
    border-radius: 9px;
    padding: 2px;
    background: #f5f7fa;
    border: 2px solid ${({ isSelected }: ListItemProps) => (isSelected ? 'var(--blue_100)' : 'transparent')};
  `,
  'blue-overlay': css`
    &:after {
      display: block;
      content: '';
      position: absolute;
      top: -6px;
      left: -6px;
      width: calc(100% + 4px);
      height: calc(100% + 4px);
      pointer-events: none;
      border: 4px solid ${({ isSelected }: ListItemProps) => (isSelected ? '#2D98D2' : 'transparent')};
      border-radius: 5px;
    }

    img {
      max-width: fit-content;
      margin: 0 auto;
    }
  `,
  '2px-overlay': css`
    &:after {
      display: block;
      content: '';
      position: absolute;
      top: -3px;
      left: -3px;
      width: calc(100% + 2px);
      height: calc(100% + 2px);
      pointer-events: none;
      border: 2px solid
        ${({ isSelected, theme }: ListItemProps) => (isSelected ? theme.selectedMediaOverlay : 'transparent')};
      border-radius: 5px;
    }

    img {
      max-width: fit-content;
      margin: 0 auto;
    }
  `,
  'with-unselect': css`
    ${blueOverlayStyles};
    ${({ isSelected }: ListItemProps) => isSelected && unselectIconStyles}
  `,
  rounded_number: css`
    &:after {
      display: block;
      position: absolute;
      top: 6px;
      right: 6px;
      width: 22px;
      height: 22px;
      font-weight: 700;
      font-size: 14px;
      line-height: 22px;
      text-align: center;
      color: white;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0px 0px 0px 2px grey;
      ${({ isSelected, order }: ListItemProps) => css`
        content: '${isSelected ? order : ''}';
        background: ${isSelected ? '#30AEF2;' : 'rgba(41, 42, 43, 0.2)'};
      `}
    }
  `,
  noselect: css``
};

export const ListItemWrapper = styled.li<ListItemProps>`
  cursor: pointer;
  position: relative;

  img {
    display: block;
    object-fit: contain;
  }

  ${({ isBackground }) =>
    isBackground &&
    css`
      img {
        background: url(${getFromCdn('images/streaming/transparent_bg.svg')}) no-repeat;
      }
    `}

  ${({ selectedItemVariant }) => variants[selectedItemVariant]}

  ${({ isPaid }) =>
    isPaid &&
    css`
      &:after {
        display: block;
        position: absolute;
        top: 6px;
        right: 6px;
        z-index: 1;
        content: '';
        width: 20px;
        height: 20px;
        background: url(${getFromCdn('images/icons/crown.svg')}) no-repeat center / cover;
      }
    `}
`;

export const ListWrapper = styled.ul<ListWrapperProps>`
  padding-top: 1px;

  #placeholder {
    opacity: 0;
  }

  ${({ isFullWidth }) =>
    !isFullWidth &&
    css`
      display: flex;
      flex-wrap: wrap;
    `}

  & > li {
    ${({ size }) => sizes[size || 'small']}
  }
`;

export const HoverIcon = styled.div<{ variant: HoverIconVariant }>`
  width: 16px;
  height: 16px;
  position: absolute;
  border-radius: 50%;
  cursor: pointer;
  background: url(${getFromCdn('images/icons/remove_icon.svg')}) no-repeat center, #919eaa;
  ${({ variant }) => hoverIconVariants[variant]}
`;

export const DragIcon = styled.div`
  width: 17px;
  height: 23px;
  top: 4px;
  right: 4px;
  z-index: 4;
  position: absolute;
  cursor: move;
  background: url(${getFromCdn('images/icons/drag.png')}) no-repeat center;
`;

export const TextWrapper = styled.div`
  /* TODO: discuss text style */
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 136px;
  height: 76px;
  padding: 10px;
  color: ${({ theme }) => theme.textColor};
`;

export const VideoPreview = styled.video.attrs({
  loop: true,
  autoPlay: true
})`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  object-fit: cover;
  ${getFadeInAnimation()}
`;

export const TitleWrapper = styled.div`
  ${getFadeInAnimation(0.3)};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(1, 1, 1, 0.3) 0%, rgba(0, 0, 0, 0.8) 100%);
  padding: 7px 35px 5px 6px;

  span {
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: #ffffff;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
